import React from "react";
import { motion } from "framer-motion";
import { useLang } from "@/i18n/LanguageContext";
import { IMG } from "@/lib/images";
import { fadeUp, stagger, viewportOnce } from "@/lib/motion";

const IMAGES = [IMG.experienceRetreat, IMG.experienceNourishment, IMG.pillar_sauna];
const ALTS = [
  "Quiet retreat space designed for movement, reflection and rest in the Norwegian landscape.",
  "Seasonal vegetables, berries and tea prepared for a Nordic retreat meal.",
  "Norwegian lake and pine forest used for outdoor recovery and nature immersion.",
];

export const Pillars = () => {
  const { t } = useLang();
  return (
    <section
      id="experience"
      data-testid="pillars-section"
      className="relative py-28 md:py-40 bg-forest text-ivory overflow-hidden"
    >
      <div className="absolute inset-0 grain" />
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12">
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          className="mb-20 max-w-3xl"
        >
          <motion.p variants={fadeUp} className="overline text-bronze mb-6" data-testid="pillars-eyebrow">
            {t.pillars.eyebrow}
          </motion.p>
          <motion.h2
            variants={fadeUp}
            className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05]"
            data-testid="pillars-title"
          >
            {t.pillars.title}
          </motion.h2>
        </motion.div>

        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          className="grid grid-cols-1 md:grid-cols-3 gap-x-10 gap-y-16"
          data-testid="pillars-grid"
        >
          {t.pillars.items.map((p, i) => (
            <motion.article
              key={p.n}
              variants={fadeUp}
              custom={i}
              className="group"
              data-testid={`pillar-card-${p.n}`}
            >
              <div className="relative aspect-[4/5] w-full overflow-hidden mb-8 spotlight">
                <motion.img
                  src={IMAGES[i]}
                  alt={ALTS[i] || p.title}
                  className="w-full h-full object-cover hover-img"
                  initial={{ clipPath: "inset(14% 14% 14% 14%)", scale: 1.12 }}
                  whileInView={{ clipPath: "inset(0% 0% 0% 0%)", scale: 1 }}
                  viewport={{ once: true, amount: 0.35 }}
                  transition={{ duration: 1.3, ease: [0.22, 1, 0.36, 1], delay: i * 0.12 }}
                />
                <span
                  className="absolute top-4 left-5 z-10 font-serif text-ivory/90 text-5xl md:text-6xl font-light leading-none pointer-events-none"
                  style={{ textShadow: "0 2px 20px rgba(15,38,29,0.6)" }}
                  aria-hidden="true"
                >
                  {p.n}
                </span>
              </div>
              <div className="flex items-baseline gap-4 mb-4 border-t border-ivory/15 pt-5 transition-colors duration-500 group-hover:border-bronze">
                <span className="overline text-bronze">{p.n}</span>
                <h3 className="font-serif text-2xl md:text-3xl font-light text-ivory">{p.title}</h3>
              </div>
              <p className="font-serif italic text-xl md:text-2xl text-ivory/95 leading-snug mb-4">
                {p.statement}
              </p>
              <p className="text-ivory/70 leading-relaxed max-w-sm text-sm md:text-base">{p.body}</p>
            </motion.article>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
